import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Layout from "components/Layout";
import ConfirmDialog from "components/ConfirmDialog";
import api from "utils/api";
import { setUser } from "state";
import { formatPrice } from "constants/massages";
import "styles/konto.css";

const formatLong = (date) =>
  new Date(date).toLocaleString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const isAwaitingPaymentBooking = (booking) => {
  const status = booking?.status;
  return (
    status === "pending-payment" ||
    status === "reserved-awaiting-payment" ||
    status === "awaiting-immediate-payment"
  );
};

const TerminePage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelId, setCancelId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [payingId, setPayingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/mine");
      setBookings(data.bookings || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Buchungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const cancelledId = searchParams.get("paymentCancelled");
    if (!cancelledId) return undefined;
    let active = true;
    (async () => {
      try {
        const { data } = await api.delete(`/bookings/${cancelledId}`);
        if (data?.user) dispatch(setUser({ user: data.user }));
        if (active) {
          setSearchParams({}, { replace: true });
          await load();
        }
      } catch {
        if (active) setSearchParams({}, { replace: true });
      }
    })();
    return () => {
      active = false;
    };
  }, [dispatch, searchParams, setSearchParams]);

  const confirmCancel = async () => {
    if (!cancelId) return;
    setCancelling(true);
    try {
      const { data } = await api.delete(`/bookings/${cancelId}`);
      if (data?.user) dispatch(setUser({ user: data.user }));
      setCancelId(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Stornierung fehlgeschlagen.");
    } finally {
      setCancelling(false);
    }
  };

  const openPayment = async (booking) => {
    if (booking?.stripeCheckoutUrl) {
      window.location.href = booking.stripeCheckoutUrl;
      return;
    }
    setPayingId(booking._id);
    setError(null);
    try {
      const { data } = await api.post(`/payment/checkout/${booking._id}`);
      if (!data?.url) {
        throw new Error("Zahlungsseite konnte nicht geöffnet werden.");
      }
      window.location.href = data.url;
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Zahlungslink konnte nicht geladen werden."
      );
      setPayingId(null);
    }
  };

  const cancelledBookingHint = (booking) => {
    if (booking.status !== "cancelled") return null;
    if (booking.cancelledBy === "customer") return "Selbst storniert";
    if (booking.cancelledBy === "admin" || booking.cancelledBy === "staff") {
      return "Vom Team storniert";
    }
    if (booking.staffCancellationReason?.trim()) return "Vom Team storniert";
    return null;
  };

  const statusLabelKonto = (booking) => {
    const status = typeof booking === "string" ? booking : booking.status;
    if (status === "pending-email-verification") {
      return "Noch nicht verbindlich (E-Mail offen)";
    }
    if (status === "pending-employee-confirmation") return "Wartet auf Bestätigung";
    if (status === "pending-payment") return "Zahlung ausstehend";
    if (status === "reserved-awaiting-payment") return "Reserviert, Zahlung ausstehend";
    if (status === "awaiting-immediate-payment") return "Sofortzahlung ausstehend";
    if (status === "confirmed") return "Bestätigt";
    if (status === "completed") return "Abgeschlossen";
    if (status === "no-show") return "Nicht erschienen";
    if (status === "expired") return "Abgelaufen";
    if (status === "cancelled") {
      const hint =
        typeof booking === "object" ? cancelledBookingHint(booking) : null;
      return hint ? `Storniert (${hint})` : "Storniert";
    }
    return status;
  };

  const paymentLabelKonto = (b) => {
    if (isAwaitingPaymentBooking(b)) return "Zahlung ausstehend (Online)";
    const paid = b.paymentStatus === "paid" || b.paymentStatus === "not-applicable";
    const paidText = paid ? "Bezahlt" : "Unbezahlt";
    let channel = "Vor Ort";
    if (b.paymentMethod === "stripe") channel = "Online";
    else if (b.paymentMethod === "later") channel = "Nach Termin";
    return `${paidText} (${channel})`;
  };

  const upcoming = bookings.filter(
    (b) => new Date(b.start) >= new Date() && b.status !== "cancelled"
  );
  const past = bookings.filter(
    (b) => new Date(b.start) < new Date() || b.status === "cancelled"
  );

  return (
    <Layout>
      <section className="konto-page">
        <div className="konto-shell">
          <header className="konto-header">
            <div>
              <h1>
                Meine <em>Termine</em>
              </h1>
              <p>
                Hier findest du deine kommenden und vergangenen Termine. Du kannst
                Buchungen ändern, stornieren oder einen neuen Termin anlegen.
              </p>
            </div>
            <Link to="/buchen" className="btn-primary">
              Neuen Termin buchen
            </Link>
          </header>

          {error && <div className="auth-alert is-error">{error}</div>}

          <h2
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--ink)",
              marginBottom: "1rem",
            }}
          >
            Kommende Termine
          </h2>
          {loading ? (
            <div className="konto-empty">Termine werden geladen…</div>
          ) : upcoming.length === 0 ? (
            <div className="konto-empty">
              <h3>Keine kommenden Termine</h3>
              <p>
                Du hast aktuell keine offenen Buchungen.{" "}
                <Link to="/buchen" style={{ color: "var(--dark-blue)", fontWeight: 600 }}>
                  Jetzt buchen →
                </Link>
              </p>
            </div>
          ) : (
            <div className="booking-list">
              {upcoming.map((b) => {
                const awaitingPayment = isAwaitingPaymentBooking(b);
                return (
                  <article
                    key={b._id}
                    className={[
                      "booking-row",
                      b.status === "cancelled" ? "is-cancelled" : "",
                      awaitingPayment ? "is-awaiting-payment" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div>
                      {awaitingPayment && (
                        <div className="booking-unpaid-banner">
                          Noch nicht bezahlt — bitte Zahlung abschließen
                        </div>
                      )}
                      <div className="booking-service">{b.serviceLabel}</div>
                      <div className="booking-meta">
                        <span>
                          <strong>Termin:</strong> {formatLong(b.start)}
                        </span>
                        <span>
                          <strong>Dauer:</strong> {b.durationMinutes} Min.
                        </span>
                        <span>
                          <strong>Preis:</strong>{" "}
                          {b.priceCents === 0 ? "Kostenfrei" : formatPrice(b.priceCents)}
                        </span>
                        <span>
                          <strong>Status:</strong> {statusLabelKonto(b)}
                        </span>
                        <span>
                          <strong>Zahlung:</strong> {paymentLabelKonto(b)}
                        </span>
                        <span>
                          <strong>Ort:</strong> {b.location}
                        </span>
                      </div>
                    </div>
                    <div className="booking-actions">
                      {awaitingPayment && (
                        <button
                          type="button"
                          className="pay-now"
                          disabled={payingId === b._id}
                          onClick={() => openPayment(b)}
                        >
                          {payingId === b._id ? "Wird geöffnet…" : "Jetzt bezahlen"}
                        </button>
                      )}
                      {!awaitingPayment && (
                        <Link to={`/konto/buchungen/${b._id}/aendern`}>Termin ändern</Link>
                      )}
                      <button
                        type="button"
                        className="danger"
                        onClick={() => setCancelId(b._id)}
                      >
                        Stornieren
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {past.length > 0 && (
            <>
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  color: "var(--ink)",
                  margin: "2.5rem 0 1rem",
                }}
              >
                Vergangene Termine
              </h2>
              <div className="booking-list">
                {past.map((b) => (
                  <article
                    key={b._id}
                    className={`booking-row ${b.status === "cancelled" ? "is-cancelled" : ""}`}
                  >
                    <div>
                      <div className="booking-service">{b.serviceLabel}</div>
                      <div className="booking-meta">
                        <span>
                          <strong>Termin:</strong> {formatLong(b.start)}
                        </span>
                        {b.status === "cancelled" && (
                          <span>
                            <strong>Status:</strong> {statusLabelKonto(b)}
                          </span>
                        )}
                        <span>
                          <strong>Preis:</strong>{" "}
                          {b.priceCents === 0 ? "Kostenfrei" : formatPrice(b.priceCents)}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>

        <ConfirmDialog
          open={Boolean(cancelId)}
          title="Buchung stornieren?"
          message="Möchtest du diese Buchung wirklich stornieren? Diese Aktion kann nicht rückgängig gemacht werden."
          confirmLabel="Stornieren"
          cancelLabel="Abbrechen"
          busy={cancelling}
          busyLabel="Wird storniert…"
          onConfirm={confirmCancel}
          onCancel={() => !cancelling && setCancelId(null)}
        />
      </section>
    </Layout>
  );
};

export default TerminePage;
